/**
 * De lo que llegó a un instante, o a NADA. La única puerta de entrada de una
 * fecha a esta aplicación.
 *
 * Existe por un reporte del dueño: «el CRM decía que hubo cambios hoy o ayer, y
 * en realidad había sido hace mucho — estaba dando datos falsos». La causa de
 * fondo era del servidor (ver `ultima_actividad` en `backend/api/v2/`), pero la
 * forma de fallar es siempre la misma y la aplicación no tenía dónde pararla:
 * `new Date(undefined)` no es una fecha inválida cualquiera, y `new Date(null)`
 * es el 1 de enero de 1970. Cada uno se dibuja como algo —«hace 56 años», o
 * peor, «recién»— y una fecha inventada que se lee como una medida es peor que
 * no tener la fecha.
 *
 * LAS TRES REGLAS, en orden:
 *
 * 1. **Ausente es ausente.** `null`, `undefined` y la cadena vacía dan `null`,
 *    nunca «ahora». Quien reciba `null` dibuja «—», que es lo que hay que decir.
 * 2. **Los segundos no son milisegundos.** Un número chico es una marca de
 *    tiempo en segundos —así las escriben Firestore, el dialer y media API del
 *    mundo—, y leerla como milisegundos la manda a enero de 1970. El corte está
 *    lejísimos de los dos usos reales: hoy en milisegundos son 1,7·10¹² y en
 *    segundos 1,7·10⁹.
 * 3. **Antes de 1990 no hay CRM.** Es el piso de credibilidad, y ataja lo que
 *    ninguna validación de forma ataja: `new Date(0)`, un `null` que se coló
 *    como número, una división por mil de más. Ninguna persona de esta base fue
 *    contactada en 1970.
 *
 * Módulo PURO y sin React: lo que decide se prueba con una tabla.
 */

/** Milisegundos por debajo de los cuales el número era, en realidad, segundos. */
const CORTE_SEGUNDOS = 1e11;

/** El piso de credibilidad: 1990-01-01T00:00:00Z. */
const PISO = Date.UTC(1990, 0, 1);

/** Sólo dígitos, con signo opcional: un número que vino escrito como texto. */
const SOLO_DIGITOS = /^-?\d+$/;

export type Instante = Date | string | number | null | undefined;

function desdeNumero(valor: number): Date | null {
  if (!Number.isFinite(valor)) return null;
  // Regla 2: un número chico son segundos. El valor absoluto, para que una
  // fecha anterior a 1970 escrita en segundos tampoco se lea como milisegundos.
  const ms = Math.abs(valor) < CORTE_SEGUNDOS ? valor * 1000 : valor;
  return new Date(ms);
}

/**
 * El instante que representa este valor, o `null` si no representa ninguno.
 *
 * `null` no es un error: es la respuesta correcta a «¿cuándo pasó esto?» cuando
 * nunca pasó. Quien pregunta decide cómo se dice —«—», una celda vacía, una
 * fila que no se dibuja—, pero nadie inventa una fecha.
 */
export function toInstante(valor: Instante): Date | null {
  if (valor === null || valor === undefined) return null;

  let fecha: Date | null;
  if (valor instanceof Date) {
    fecha = valor;
  } else if (typeof valor === 'number') {
    fecha = desdeNumero(valor);
  } else {
    const limpio = valor.trim();
    // Regla 1: la cadena vacía es ausencia, y `new Date('')` no lo dice —lo
    // dice por accidente, con un `Invalid Date` que alguien puede olvidar mirar.
    if (!limpio) return null;
    fecha = SOLO_DIGITOS.test(limpio) ? desdeNumero(Number(limpio)) : new Date(limpio);
  }

  if (fecha === null) return null;
  const ms = fecha.getTime();
  if (Number.isNaN(ms)) return null;
  // Regla 3.
  if (ms < PISO) return null;
  return fecha;
}
